import { useSyncExternalStore } from "react";

/**
 * Captures the `beforeinstallprompt` event so the user can install the
 * app from a UI button rather than the browser's infobar.
 *
 * On iOS/Safari this event never fires — the hook detects that platform
 * and exposes `isIOS` so the UI can show "Share → Add to Home Screen"
 * instructions instead of a clickable install button.
 *
 * Detection of "already installed" uses the `display-mode: standalone`
 * media query — when the app is running standalone (from home screen),
 * `canInstall` is false and the button is hidden.
 *
 * ── Why the listener lives at module level ──────────────────────────
 *
 * Chrome fires `beforeinstallprompt` very early during page load —
 * immediately after verifying the PWA criteria (manifest, service
 * worker, HTTPS).  The `SettingsMenu` (the only consumer of this hook)
 * only mounts *after* auth completes, which is seconds later.  If the
 * listener lived inside a React `useEffect`, the event would fire and
 * be missed before the component ever mounted.
 *
 * By registering the listener at module scope (the moment the JS bundle
 * loads) and sharing the captured event via a `useSyncExternalStore`
 * singleton, every component that calls the hook — no matter how late
 * it mounts — sees the already-captured event.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone(): boolean {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      // iOS Safari uses a different property
      (window.navigator as any).standalone === true
    );
  } catch {
    return false;
  }
}

function detectIOS(): boolean {
  try {
    const ua = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    // iPadOS 13+ reports as Macintosh, so also check for Mac + touch
    const isIPadOS = /Macintosh/.test(ua) && "ontouchend" in document;
    return isIOS || isIPadOS;
  } catch {
    return false;
  }
}

// ── Module-level singleton ───────────────────────────────────────────
//
// These live outside React so the `beforeinstallprompt` listener is
// active from the moment the module is imported (bundle load time),
// not from when a component mounts.

const STANDALONE = isStandalone();
const IOS = detectIOS();

let deferredEvent: BeforeInstallPromptEvent | null = null;
let userChoiceOutcome: "accepted" | "dismissed" | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const l of listeners) l();
}

function subscribeStore(callback: () => void): () => void {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): number {
  // Return a number that changes whenever the relevant state changes.
  // useSyncExternalStore requires a stable reference or primitive that
  // differs when the snapshot changes — we encode the state as a bitfield.
  return (deferredEvent ? 1 : 0) | (userChoiceOutcome ? 2 : 0);
}

function getServerSnapshot(): number {
  return 0;
}

// Register the listener once, at module load time.  This runs before any
// component mounts, so the event is never missed.
if (typeof window !== "undefined" && !STANDALONE) {
  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    deferredEvent = e as BeforeInstallPromptEvent;
    userChoiceOutcome = null;
    notify();
  });

  // Clear the event after the app is installed — `appinstalled` fires
  // once the user completes the install dialog.
  window.addEventListener("appinstalled", () => {
    deferredEvent = null;
    notify();
  });
}

export interface InstallPromptAPI {
  /** True when the browser has fired beforeinstallprompt and the app
   *  is not already running standalone. */
  canInstall: boolean;
  /** True on iOS/Safari where beforeinstallprompt never fires. */
  isIOS: boolean;
  /** True when the app is already running in standalone mode. */
  isInstalled: boolean;
  /** Call to trigger the native install prompt. No-op if canInstall is false. */
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

export function useInstallPrompt(): InstallPromptAPI {
  useSyncExternalStore(subscribeStore, getSnapshot, getServerSnapshot);

  const promptInstall = async (): Promise<
    "accepted" | "dismissed" | "unavailable"
  > => {
    if (!deferredEvent) return "unavailable";
    await deferredEvent.prompt();
    const choice = await deferredEvent.userChoice;
    // Clear the event after the user responds — Chrome only fires
    // beforeinstallprompt once (until the user clears site data).
    deferredEvent = null;
    userChoiceOutcome = choice.outcome;
    notify();
    return choice.outcome;
  };

  return {
    canInstall: !STANDALONE && deferredEvent !== null,
    isIOS: IOS,
    isInstalled: STANDALONE,
    promptInstall,
  };
}
